'use client'

import { useEffect, useRef, useState } from 'react'
import Image from 'next/image'
import Link from 'next/link'

import { getArticleUrl } from '@/lib/api-connector-strapi-cms/utils/getArticleUrl.utils'
import { ArticlesPromotedListItemType } from '@/lib/models/components/articlesPromotedList.types'
import { Markdown } from '@/lib/ui-kit/components/Markdown.component'

export const Title = ({ children }: { children?: React.ReactNode }) => {
  return <h1 className="heading-l pb-2 font-medium">{children}</h1>
}

export const CardArticlePromoted = ({
  category,
  title,
  description,
  authorName,
  datePublication,
  linkArticle,
}: ArticlesPromotedListItemType) => {
  return (
    <Link
      href={getArticleUrl(linkArticle)}
      className="card-article-promoted group flex h-[16rem] cursor-pointer border-b border-gray-400 p-6 xl:h-[18rem] xl:justify-between xl:px-0 xl:py-8"
    >
      <div className="flex h-full flex-1 flex-col justify-between pr-10">
        <div className="caption pb-4">{category}</div>
        <div className="card-article-promoted-title paragraph-xxl mb-2 font-semibold">
          {title}
        </div>
        <div className="paragraph-s pb-6 text-gray-800 xl:pb-8">{`${authorName}  •  ${datePublication}`}</div>
        <div className="card-article-promoted-text paragraph-s">
          {description}
        </div>
      </div>
    </Link>
  )
}

export const SectionArticlesPromoted = ({
  initialData,
  apiAssetsUrl,
}: {
  initialData: any
  apiAssetsUrl: string | undefined
}) => {
  const [data] = useState(initialData)
  const sectionRef = useRef<HTMLElement>(null)
  const asideRef = useRef<HTMLElement>(null)

  const { articlePromoted, articlesPromoted } = data

  useEffect(() => {
    const handleWheel = (e: WheelEvent) => {
      if (asideRef.current) {
        const { scrollTop, scrollHeight, clientHeight } = asideRef.current
        const isAtBottom = scrollTop + clientHeight >= scrollHeight - 1
        const isAtTop = scrollTop <= 0

        if ((isAtBottom && e.deltaY > 0) || (isAtTop && e.deltaY < 0)) {
          return
        }

        asideRef.current.scrollBy(0, e.deltaY)
        e.preventDefault()
      }
    }

    const section = sectionRef.current
    if (section) {
      section.addEventListener('wheel', handleWheel, { passive: false })
    }

    return () => {
      if (section) {
        section.removeEventListener('wheel', handleWheel)
      }
    }
  }, [])

  return (
    <section
      ref={sectionRef}
      className="relative mx-auto grid max-w-[1440px] grid-cols-1 py-10 pt-48 xl:grid-cols-3 xl:gap-10 xl:pt-48"
    >
      <div className="absolute flex w-full justify-center xl:pr-40 xl:pt-56">
        <div className="gradient-pink-yellow-circle gpu-access-and-gradients-adjustment" />
      </div>
      <article className="relative col-span-2 border-b border-gray-400 pb-8 xl:border-none">
        {articlePromoted && (
          <Link
            href={getArticleUrl(articlePromoted.linkArticle)}
            className="cursor-pointer"
          >
            <Image
              className="w-full pb-6 xl:hidden"
              src={`${apiAssetsUrl || ''}${articlePromoted.imageMain.url}`}
              width={articlePromoted.imageMain.width}
              height={articlePromoted.imageMain.height}
              alt={
                articlePromoted.imageMain.alternativeText ||
                `Image of ${articlePromoted.title}`
              }
              quality={100}
              unoptimized={true}
            />
            <Image
              className="hidden xl:block xl:pb-10"
              src={`${apiAssetsUrl || ''}${articlePromoted.imageMainXL.url}`}
              width={articlePromoted.imageMainXL.width}
              height={articlePromoted.imageMainXL.height}
              alt={
                articlePromoted.imageMainXL.alternativeText ||
                `Image of ${articlePromoted.title}`
              }
              quality={100}
              unoptimized={true}
            />
            <div className="px-6 xl:px-10">
              <div className="caption pb-4">{articlePromoted.category}</div>
              <Markdown
                components={{
                  p: Title,
                }}
              >
                {articlePromoted.title}
              </Markdown>
              <div className="paragraph-s pb-6 text-gray-800 xl:pb-8">{`${articlePromoted.authorName}  •  ${articlePromoted.datePublication}`}</div>
              <p className="paragraph-l max-w-xl text-gray-1000">
                {articlePromoted.description}
              </p>
            </div>
          </Link>
        )}
      </article>
      {articlesPromoted && (
        <aside
          ref={asideRef}
          className="custom-scrollbar relative max-h-screen overflow-y-auto xl:pb-10"
        >
          {articlesPromoted.map((article: any) => {
            return (
              <CardArticlePromoted
                id={article.id}
                key={article.id}
                category={article.category}
                title={article.title}
                description={article.description}
                authorName={article.authorName}
                datePublication={article.datePublication}
                linkArticle={article.linkArticle}
              />
            )
          })}
        </aside>
      )}
    </section>
  )
}
